import { Box, CircularProgress, Fade, Icon, Stack, Typography } from '@mui/material'
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { SSE, SSEvent } from 'sse.js'
import { FloatingStatusPortal } from '~/components/FloatingStatusPortal/FloatingStatusPortal'
import { getToken } from '~/data/api'
import { useUploadRecordFile } from '~/modules/inspection/pages/FillReport/Records/data/useData'
import { QueueFile, useRecordClassificationQueueStore } from '~/stores/record-classification.store'
import { API_URL } from '~/utils/consts'
import { IRecordFile, IRecordFileClassificationSSEMessage } from '~/utils/types'
interface IRecordFileClassificationQueueStats {
  total: number
  pending: number
  uploading: number
  classifying: number
  completed: number
  failed: number
}
interface IRecordClassificationQueueContext {
  stats: IRecordFileClassificationQueueStats
}

const RecordClassificationQueueContext = createContext<
  IRecordClassificationQueueContext | undefined
>(undefined)

interface IProviderProps {
  children: ReactNode
}
export function RecordClassificationQueueProvider({ children }: Readonly<IProviderProps>) {
  const { stats } = useRecordFileClassificationEngine({})
  const isQueueActive = useRecordClassificationQueueStore((state) => state.isQueueActive())

  const memoizedStats = useMemo(() => stats, [stats])

  return (
    <RecordClassificationQueueContext.Provider value={{ stats: memoizedStats }}>
      {children}
      <FloatingStatusPortal>
        <Fade in={isQueueActive}>
          <Box>
            <RecordClassificationFloatingStatus />
          </Box>
        </Fade>
      </FloatingStatusPortal>
    </RecordClassificationQueueContext.Provider>
  )
}

export function useRecordClassificationQueueContext() {
  const context = useContext(RecordClassificationQueueContext)
  if (!context) {
    throw new Error(
      'useRecordClassificationQueueContext must be used within a RecordClassificationQueueProvider',
    )
  }
  return context
}

interface UseRecordFileClassificationQueueParams {
  onAllComplete?: () => void
  onFileComplete?: (fileId: string) => void
  onFileError?: (fileId: string, error: Error) => void
}

export function useRecordFileClassificationEngine({
  onAllComplete,
  onFileComplete,
  onFileError,
}: UseRecordFileClassificationQueueParams) {
  const {
    queue,
    getActive,
    maxConcurrency,
    autoStart,
    updateFileStatus,
    updateFileStatusByRecordId,
  } = useRecordClassificationQueueStore()
  const currentReportId = useRecordClassificationQueueStore((state) => state.getCurrentReportId())

  const uploadMutation = useUploadRecordFile()

  const isProcessingRef = useRef(false)

  const batchUpload = useCallback(async () => {
    const pendingFiles = queue.filter((qf) => qf.status === 'queue')
    const uploadingCount = queue.filter((qf) => qf.status === 'uploading').length

    const availableSlots = maxConcurrency - uploadingCount

    // Exit conditions: no slots available or no pending files
    if (availableSlots <= 0 || pendingFiles.length === 0) {
      // Check if all processing is complete
      if (uploadingCount === 0 && pendingFiles.length === 0) {
        const hasActiveFiles = getActive().length > 0

        // If no files are being processed, we're done
        if (!hasActiveFiles && queue.length > 0) {
          onAllComplete?.()
        }
      }
      return
    }

    // Mark as processing
    isProcessingRef.current = true

    try {
      const uploadBatch = pendingFiles.slice(0, availableSlots)

      const uploadPromises = uploadBatch.map((item) =>
        uploadMutation
          .mutateAsync({
            report_id: item.reportId,
            file: item.file,
            onUploadProgress: (progress) => {
              // Update progress in real-time
              updateFileStatus(item.id, 'uploading', {
                uploadProgress: progress * 100,
              })
            },
            auto_classify: true,
          })
          .then((response: IRecordFile) => {
            updateFileStatus(item.id, 'classifying', {
              classificationStatus: 'queue',
              recordFileId: response._id,
            })
            onFileComplete?.(item.id)
            return response
          })
          .catch((error) => {
            // Upload failed - mark as error
            console.error('Upload failed for:', item.id, error)
            updateFileStatus(item.id, 'err_could_not_upload')
            onFileError?.(item.id, error)
            throw error
          }),
      )

      await Promise.allSettled(uploadPromises)
    } finally {
      isProcessingRef.current = false
    }
  }, [
    queue,
    maxConcurrency,
    uploadMutation,
    updateFileStatus,
    getActive,
    onAllComplete,
    onFileComplete,
    onFileError,
  ])

  /**
   * Get queue statistics
   */
  const stats = useMemo(
    (): IRecordFileClassificationQueueStats => ({
      total: queue.length,
      pending: queue.filter((qf) => qf.status === 'queue').length,
      uploading: queue.filter((qf) => qf.status === 'uploading').length,
      classifying: queue.filter((qf) => qf.status === 'classifying').length,
      completed: queue.filter((qf) => qf.status === 'done').length,
      failed: queue.filter((qf) => qf.status === 'err_could_not_upload').length,
    }),
    [queue],
  )

  useEffect(() => {
    if (autoStart && isProcessingRef.current === false && stats.pending > 0) {
      batchUpload()
    }
  }, [autoStart, batchUpload, stats.pending])

  // SSE Updates with automatic reconnection
  useEffect(() => {
    if (!currentReportId) {
      return
    }

    let source: SSE | null = null
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null
    let reconnectAttempts = 0
    let isMounted = true
    const MAX_RECONNECT_ATTEMPTS = 10
    const BASE_RECONNECT_DELAY = 1000 // 1 second

    const connectSSE = () => {
      if (!isMounted) return

      source = new SSE(`${API_URL}/report/${currentReportId}/sse`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      })

      source.addEventListener('status', function (e: SSEvent) {
        try {
          reconnectAttempts = 0 // Reset on successful message
          const message = JSON.parse(e.data) as IRecordFileClassificationSSEMessage

          const recordFileId = message.record_file_id
          if (!recordFileId) return

          updateFileStatusByRecordId(recordFileId, 'classifying', {
            classificationStatus: message.data.status,
          })
        } catch (e) {
          console.log(`Error while parsing SSE data from report ${currentReportId}`)
          console.error(e)
        }
      })

      source.addEventListener('result', function (e: SSEvent) {
        try {
          reconnectAttempts = 0 // Reset on successful message
          const message = JSON.parse(e.data) as IRecordFileClassificationSSEMessage

          const recordFileId = message.record_file_id
          if (!recordFileId) return

          updateFileStatusByRecordId(recordFileId, 'done', {
            classificationStatus: message.data.status,
            remoteReason: message.data.reason,
            result: {
              category: {
                final: message.data.result?.record_category?.final_classification ?? undefined,
                suggestion:
                  message.data.result?.record_category?.new_classify_option_suggestion ?? undefined,
              },
              fileClass: {
                final: message.data.result?.file_class?.final_classification ?? undefined,
                suggestion:
                  message.data.result?.file_class?.new_classify_option_suggestion ?? undefined,
              },
            },
          })
        } catch (e) {
          console.log(`Error while parsing SSE data from report ${currentReportId}`)
          console.error(e)
        }
      })

      source.addEventListener('error', (e: Error) => {
        console.log('Error while connecting to report SSE')
        console.error(e)

        // Attempt reconnection with exponential backoff
        if (reconnectAttempts < MAX_RECONNECT_ATTEMPTS && isMounted) {
          const delay = Math.min(BASE_RECONNECT_DELAY * Math.pow(2, reconnectAttempts), 30000)
          reconnectAttempts++

          console.log(
            `Attempting SSE reconnection ${reconnectAttempts}/${MAX_RECONNECT_ATTEMPTS} in ${delay}ms`,
          )

          if (source) {
            source.close()
            source = null
          }

          reconnectTimer = setTimeout(() => {
            if (isMounted) {
              connectSSE()
            }
          }, delay)
        } else if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
          console.error('SSE max reconnection attempts reached')
        }
      })

      source.stream()
    }

    connectSSE()

    return () => {
      isMounted = false
      if (reconnectTimer) {
        clearTimeout(reconnectTimer)
      }
      if (source) {
        source.close()
      }
    }
  }, [currentReportId, updateFileStatusByRecordId])

  return {
    batchUpload,
    stats,

    /**
     * Whether the upload mutation is in a loading state
     */
    isMutating: uploadMutation.isLoading,
  }
}

export const RecordClassificationFloatingStatus = () => {
  const queue = useRecordClassificationQueueStore((state) => state.queue)
  const currentWindowLocation = useRecordClassificationQueueStore((state) =>
    state.getWindowLocation(),
  )

  const { stats } = useRecordClassificationQueueContext()
  const { t } = useTranslation()

  const currentProgress = useMemo(() => {
    const maxProgress = queue.length * 100

    const queueProgress = queue
      .map(getItemTotalProgress)
      .reduce((partialSum, a) => partialSum + a, 0)

    return (queueProgress / maxProgress) * 100
  }, [queue])

  const nonConcludedQueueFiles = useMemo(
    () => stats.pending + stats.uploading + stats.classifying,
    [stats],
  )

  const navigate = useNavigate()

  return (
    <div
      className='min-w-[300px] rounded-lg backdrop-blur-xl overflow-hidden'
      style={{
        background:
          'linear-gradient(135deg, rgba(15, 23, 42, 0.95), rgba(30, 41, 59, 0.9), rgba(51, 65, 85, 0.9), rgba(15, 23, 42, 0.95))',
        backgroundSize: '400% 400%',
        animation: 'snow-glass-gradient 20s ease-in-out infinite',
        border: '1px solid rgba(148, 163, 184, 0.2)',
        boxShadow:
          '0 4px 5px rgba(15, 23, 42, 0.6), 0 3px 5px rgba(30, 41, 59, 0.4), 0 12px 60px rgba(51, 65, 85, 0.3)',
      }}
      onClick={() => {
        if (!currentWindowLocation) return

        navigate(currentWindowLocation)
      }}
    >
      <Stack direction='row' alignItems='center' px={1}>
        <Icon
          sx={{
            color: 'rgba(59, 130, 246, 0.9)',
            fontSize: 32,
            zIndex: 1,
          }}
        >
          assistant
        </Icon>
        <Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ p: 2 }}>
          <div>
            <Typography
              fontSize={14}
              variant='caption'
              className='w-full'
              textAlign={'justify'}
              sx={{ color: 'rgba(226, 232, 240, 0.95)', fontWeight: 500 }}
            >
              {t('Classifying records')}
            </Typography>
            <Typography
              fontSize={10}
              variant='caption'
              className='w-full'
              textAlign={'justify'}
              sx={{ display: 'inline-block', color: 'rgba(148, 163, 184, 0.9)' }}
            >
              {nonConcludedQueueFiles} {t('Pending files')}
            </Typography>
          </div>
          <div className='relative inline-flex'>
            <CircularProgress
              variant='determinate'
              value={currentProgress}
              size={28}
              sx={{
                color: 'rgba(59, 130, 246, 0.9)',
                '& .MuiCircularProgress-circle': {
                  strokeLinecap: 'round',
                },
              }}
            />
            <div className='top-0 left-0 right-0 bottom-0 absolute flex items-center justify-center'>
              <Typography
                variant='caption'
                component='div'
                sx={{ color: 'rgba(226, 232, 240, 0.95)', fontSize: 9, fontWeight: 600 }}
              >{`${Math.round(currentProgress)}%`}</Typography>
            </div>
          </div>
        </Stack>
      </Stack>
    </div>
  )
}

export function getItemTotalProgress(item: QueueFile): number {
  // Upload Part
  const uploadPart: number = item.uploadProgress ?? 0

  // Classification Part
  let classificationPart = 0
  switch (item.classificationStatus) {
    case 'queue': {
      break
    }
    case 'starting': {
      classificationPart = 25
      break
    }
    case 'reading_file': {
      classificationPart = 50
      break
    }
    case 'classifying_category': {
      classificationPart = 75
      break
    }
    case 'classifying_file_class': {
      classificationPart = 99
      break
    }

    case 'done': {
      classificationPart = 100
      break
    }
  }

  return uploadPart / 2 + (classificationPart ? classificationPart / 2 : 0)
}
